/**
 * PRAT-187: preço de atacado por faixa — prévia no app (a API recalcula no
 * pedido com a mesma conta, WholesalePriceCalculator).
 *
 * Por família: unidades = soma das linhas dos produtos dela; vale o menor preço
 * entre as faixas com min_qty <= unidades. O app não sabe quem é revendedor,
 * então só mostra as faixas para todos (a de revendedor sai no pedido).
 * `pack_only`: a família só vende em caixa fechada (múltiplo de pack_size).
 */
export type WholesaleTier = { min_qty: number; unit_price: number; audience: 'all' | 'reseller' };

export type WholesaleFamily = {
  id?: string;
  name: string;
  item_ids: Array<string | number>;
  tiers: WholesaleTier[];
  pack_size?: number | null;
  pack_only?: boolean;
};

export type WholesaleRule = { families: WholesaleFamily[] };

export type WholesaleLine = {
  key: string;
  item_id: string;
  quantity: number;
  unit_price: number;
  weighted?: boolean;
};

export type WholesaleFamilyResult = {
  name: string;
  units: number;
  unit_price: number | null;
  discount: number;
  next_min_qty: number | null;
  next_unit_price: number | null;
};

export type WholesaleResult = {
  total: number;
  lines: Record<string, number>;
  families: WholesaleFamilyResult[];
};

const round2 = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

/** Faixas que o cliente do app enxerga, da menor quantidade para a maior. */
export function publicTiers(family: WholesaleFamily): WholesaleTier[] {
  return (family.tiers ?? [])
    .filter(t => (t.audience ?? 'all') !== 'reseller' && Number(t.unit_price) >= 0)
    .sort((a, b) => Number(a.min_qty) - Number(b.min_qty));
}

export function familyForItem(
  rule: WholesaleRule | null | undefined,
  itemId: string | number | null | undefined
): WholesaleFamily | null {
  if (!rule || itemId === null || itemId === undefined) return null;
  return (
    (rule.families ?? []).find(f => (f.item_ids ?? []).map(String).includes(String(itemId))) ?? null
  );
}

export function tierPriceFor(family: WholesaleFamily, units: number): number | null {
  const prices = publicTiers(family)
    .filter(t => Number(t.min_qty) <= units)
    .map(t => Number(t.unit_price));
  return prices.length ? round2(Math.min(...prices)) : null;
}

/** Passo do seletor: a caixa inteira quando a família só vende em caixa fechada. */
export function quantityStep(family: WholesaleFamily | null): number {
  const pack = Number(family?.pack_size) || 0;
  return family?.pack_only && pack > 1 ? pack : 1;
}

/**
 * Quantidade válida mais próxima (para cima): a menor que fecha a caixa com o
 * que a família já tem na sacola e, daí, de caixa em caixa. Sem caixa
 * fechada, qualquer quantidade a partir de 1.
 */
export function packQuantity(
  family: WholesaleFamily | null,
  unitsInCart: number,
  wanted: number
): number {
  const step = quantityStep(family);
  const n = Math.max(1, Math.trunc(Number(wanted) || 0));
  if (step < 2) return n;
  const missing = Math.trunc(unitsInCart) % step;
  const min = missing > 0 ? step - missing : step;
  return n <= min ? min : min + Math.ceil((n - min) / step) * step;
}

/** Menor preço por unidade que o cliente do app alcança (o "a partir de"). */
export function lowestTierPrice(family: WholesaleFamily | null, basePrice: number): number | null {
  if (!family) return null;
  const prices = publicTiers(family).map(t => Number(t.unit_price));
  const lowest = prices.length ? round2(Math.min(...prices)) : null;
  return lowest !== null && lowest < basePrice ? lowest : null;
}

export function calculateWholesale(
  rule: WholesaleRule | null | undefined,
  lines: WholesaleLine[]
): WholesaleResult {
  const result: WholesaleResult = { total: 0, lines: {}, families: [] };
  for (const family of rule?.families ?? []) {
    const ids = new Set((family.item_ids ?? []).map(String));
    const members = lines.filter(
      l => !l.weighted && ids.has(String(l.item_id)) && l.unit_price > 0 && l.quantity > 0
    );
    if (!members.length) continue;
    const units = members.reduce((sum, l) => sum + Math.trunc(l.quantity), 0);
    const price = tierPriceFor(family, units);
    const next = publicTiers(family).find(t => Number(t.min_qty) > units);
    let familyTotal = 0;
    if (price !== null) {
      for (const line of members) {
        const discount = round2(Math.max(0, line.unit_price - price) * Math.trunc(line.quantity));
        if (discount <= 0) continue;
        result.lines[line.key] = round2((result.lines[line.key] ?? 0) + discount);
        familyTotal += discount;
      }
    }
    result.families.push({
      name: family.name,
      units,
      unit_price: price,
      discount: round2(familyTotal),
      next_min_qty: next ? Number(next.min_qty) : null,
      next_unit_price: next ? Number(next.unit_price) : null,
    });
  }
  result.total = round2(Object.values(result.lines).reduce((a, b) => a + b, 0));
  return result;
}

/** Mesma mensagem da API: família "só caixa fechada" fora do múltiplo. */
export function wholesalePackViolations(
  rule: WholesaleRule | null | undefined,
  lines: Array<Pick<WholesaleLine, 'item_id' | 'quantity' | 'weighted'>>
): string[] {
  const out: string[] = [];
  for (const family of rule?.families ?? []) {
    const pack = quantityStep(family);
    if (pack < 2) continue;
    const ids = new Set((family.item_ids ?? []).map(String));
    const units = lines
      .filter(l => !l.weighted && ids.has(String(l.item_id)))
      .reduce((sum, l) => sum + Math.trunc(Number(l.quantity) || 0), 0);
    if (units > 0 && units % pack !== 0) {
      const down = Math.floor(units / pack) * pack;
      const up = down + pack;
      out.push(
        `${family.name} é vendido de ${pack} em ${pack} un. Você tem ${units} un: ${
          down > 0 ? `ajuste para ${down} ou ${up}.` : `complete ${up}.`
        }`
      );
    }
  }
  return out;
}

/** "Faltam 12 un para R$ 1,80/un" — próxima faixa de cada família. */
export function wholesaleHint(result: WholesaleResult): string | null {
  const parts = result.families
    .filter(f => f.next_min_qty !== null && f.next_unit_price !== null)
    .map(
      f =>
        `${f.name}: faltam ${Number(f.next_min_qty) - f.units} un para R$ ${Number(
          f.next_unit_price
        )
          .toFixed(2)
          .replace('.', ',')}/un`
    );
  return parts.length ? parts.join(' · ') : null;
}
